import {
  Container,
  Box,
  Flex,
  Text,
  VStack,
  Badge,
  Icon,
  CircularProgress,
  CircularProgressLabel,
  Spinner,
  Button,
  Select,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Wind, Cloud, AlertCircle, CheckCircle, Info, RefreshCw, Thermometer, Droplets, Gauge, MapPin } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { Copyright } from "../../components";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";
import { useAirQuality } from "../../hooks/useAirQuality";
import { If, Then, Else } from "react-if";

const MotionBox = motion(Box);

// Funkcja do określenia poziomu jakości powietrza
const getAQILevel = (aqi: number): { level: string; color: string; description: string; icon: any } => {
  if (aqi <= 50) {
    return {
      level: "Dobra",
      color: "#10B981",
      description: "Jakość powietrza jest zadowalająca, zanieczyszczenie powietrza stanowi niewielkie ryzyko.",
      icon: CheckCircle,
    };
  } else if (aqi <= 100) {
    return {
      level: "Umiarkowana",
      color: "#F59E0B",
      description: "Jakość powietrza jest akceptowalna, jednak niektóre zanieczyszczenia mogą być szkodliwe dla osób wrażliwych.",
      icon: Info,
    };
  } else if (aqi <= 150) {
    return {
      level: "Niezdrowa dla wrażliwych",
      color: "#F97316",
      description: "Osoby wrażliwe mogą odczuwać problemy zdrowotne. Ogół społeczeństwa prawdopodobnie nie odczuje żadnych skutków.",
      icon: AlertCircle,
    };
  } else if (aqi <= 200) {
    return {
      level: "Niezdrowa",
      color: "#EF4444",
      description: "Każdy może zacząć odczuwać problemy zdrowotne. Osoby wrażliwe mogą odczuwać poważniejsze skutki.",
      icon: AlertCircle,
    };
  } else if (aqi <= 300) {
    return {
      level: "Bardzo niezdrowa",
      color: "#991B1B",
      description: "Ostrzeżenie zdrowotne: każdy może odczuwać poważniejsze problemy zdrowotne.",
      icon: AlertCircle,
    };
  } else {
    return {
      level: "Niebezpieczna",
      color: "#7C2D12",
      description: "Ostrzeżenie zdrowotne: wszyscy mogą odczuwać poważne problemy zdrowotne.",
      icon: AlertCircle,
    };
  };
};

const HeaderContainer = styled.header<{ $theme: AppTheme }>`
  background: ${({ $theme }) => $theme.gradientHeader};
  padding: 2.5rem 1rem;
  position: relative;
  overflow: hidden;
`;

const HeaderDecoration = styled.div<{ $theme: AppTheme }>`
  position: absolute;
  top: -60%;
  right: -25%;
  width: 350px;
  height: 350px;
  border-radius: 50%;
  background: ${({ $theme }) => `${$theme.primary}15`};
  filter: blur(40px);
`;

const HeaderDecorationSmall = styled.div<{ $theme: AppTheme }>`
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: ${({ $theme }) => `${$theme.accent}10`};
  filter: blur(30px);
`;

// Funkcja do określania koloru na podstawie wartości i progu
const getValueColor = (value: number, threshold: number, theme: AppTheme): string => {
  return value > threshold ? theme.warning : theme.success;
};

const AirQualityCard = ({ label, value, unit, theme, color }: { 
  label: string; 
  value: number; 
  unit: string; 
  theme: AppTheme;
  color?: string;
}) => {
  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      bg={theme.backgroundCard}
      borderRadius={THEME.borderRadiusLg}
      boxShadow={theme.shadowMd}
      border={`1px solid ${theme.borderCard}`}
      p={5}
      _hover={{
        boxShadow: theme.shadowLg,
        transform: "translateY(-2px)",
        borderColor: color ? `${color}40` : `${theme.primary}40`,
      }}
      style={{ transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }}
    >
      <Text
        fontFamily="'Outfit', sans-serif"
        fontSize="sm"
        fontWeight="600"
        color={theme.textMuted}
        mb={2}
        textTransform="uppercase"
        letterSpacing="0.05em"
      >
        {label}
      </Text>
      <Flex align="baseline" gap={2}>
        <Text
          fontFamily="'Outfit', sans-serif"
          fontSize="2xl"
          fontWeight="700"
          color={color || theme.text}
        >
          {value}
        </Text>
        <Text
          fontSize="sm"
          color={theme.textMuted}
          fontWeight="500"
        >
          {unit}
        </Text>
      </Flex>
    </MotionBox>
  );
};

const LoadingScreen = ({ theme }: { theme: AppTheme }) => (
  <Container maxW="container.md" py={8} px={4}>
    <Flex direction="column" align="center" justify="center" minH="50vh" gap={4}>
      <Spinner
        thickness="4px"
        speed="0.65s"
        emptyColor={theme.backgroundMuted}
        color={theme.primary}
        size="xl"
      />
      <Text color={theme.textSecondary} fontSize="sm" fontWeight="500">
        Pobieranie danych o jakości powietrza...
      </Text>
    </Flex>
  </Container>
);

const ErrorScreen = ({ theme, onRetry }: { theme: AppTheme; onRetry: () => void }) => (
  <Container maxW="container.md" py={8} px={4}>
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      bg={theme.backgroundCard}
      borderRadius={THEME.borderRadiusXl}
      boxShadow={theme.shadowLg}
      border={`1px solid ${theme.borderCard}`}
      p={8}
      textAlign="center"
    >
      <Icon as={AlertCircle} boxSize={12} color={theme.red} mb={4} />
      <Text
        fontFamily="'Outfit', sans-serif"
        fontSize="lg"
        fontWeight="700"
        color={theme.text}
        mb={2}
      >
        Nie udało się pobrać danych
      </Text>
      <Text color={theme.textSecondary} fontSize="sm" mb={6}>
        Wystąpił problem podczas pobierania danych o jakości powietrza. Sprawdź połączenie internetowe i spróbuj ponownie.
      </Text>
      <Button
        onClick={onRetry}
        bg={theme.primary}
        color="white"
        borderRadius={THEME.borderRadiusFull}
        fontWeight="600"
        leftIcon={<RefreshCw size={16} />}
        _hover={{ bg: theme.primaryDark }}
      >
        Spróbuj ponownie
      </Button>
    </MotionBox>
  </Container>
);

// Komponent nagłówka
const PageHeader = ({ 
  theme, 
  location, 
  availableLocations, 
  currentLocationId, 
  onLocationChange 
}: { 
  theme: AppTheme; 
  location?: string;
  availableLocations: any[];
  currentLocationId: string;
  onLocationChange: (locationId: string) => void;
}) => (
  <HeaderContainer $theme={theme}>
    <HeaderDecoration $theme={theme} />
    <HeaderDecorationSmall $theme={theme} />
    <Container maxW="container.md" position="relative" zIndex={1}>
      <MotionBox
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Flex align="center" gap={3} mb={3}>
          <Box
            p={3}
            borderRadius={THEME.borderRadius}
            bg="rgba(255,255,255,0.15)"
            backdropFilter="blur(10px)"
            border="1px solid rgba(255,255,255,0.2)"
          >
            <Icon as={Wind} boxSize={6} color="white" />
          </Box>
          <Text
            fontFamily="'Outfit', sans-serif"
            fontSize="2xl"
            fontWeight="800"
            color="white"
            letterSpacing="-0.02em"
          >
            Jakość powietrza
          </Text>
          <Icon as={Cloud} boxSize={5} color="white" opacity={0.7} />
        </Flex>
        <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} mt={2}>
          {location && (
            <Text color="whiteAlpha.800" fontSize="sm" fontWeight="500" pl={1}>
              {location}
            </Text>
          )}
          <Box flex="1" minW="200px" maxW="300px">
            <Select
              value={currentLocationId}
              onChange={(e) => onLocationChange(e.target.value)}
              bg="rgba(255,255,255,0.15)"
              backdropFilter="blur(10px)"
              border="1px solid rgba(255,255,255,0.3)"
              color="white"
              _hover={{
                bg: "rgba(255,255,255,0.2)",
                borderColor: "rgba(255,255,255,0.4)",
              }}
              _focus={{
                bg: "rgba(255,255,255,0.2)",
                borderColor: "rgba(255,255,255,0.5)",
                boxShadow: "0 0 0 3px rgba(255,255,255,0.2)",
              }}
              icon={<Icon as={MapPin} color="white" />}
              size="sm"
            >
              {availableLocations.map((loc) => (
                <option key={loc.id} value={loc.id} style={{ background: theme.backgroundCard, color: theme.text }}>
                  {loc.name}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>
      </MotionBox>
    </Container>
  </HeaderContainer>
);

export function AirQualityPage() {
  const theme = useTheme() as AppTheme;
  const { 
    airQuality, 
    isLoading, 
    isFromCache, 
    currentLocation,
    availableLocations,
    setLocation,
    refetch 
  } = useAirQuality();
  
  // Jeśli nie ma danych, pokaż ekran ładowania lub błędu
  if (!airQuality) {
    return (
      <Box minH="100vh" bg={theme.background}>
        <PageHeader 
          theme={theme} 
          location={currentLocation.name}
          availableLocations={availableLocations}
          currentLocationId={currentLocation.id}
          onLocationChange={setLocation}
        />
        <If condition={isLoading}>
          <Then>
            <LoadingScreen theme={theme} />
          </Then>
          <Else>
            <ErrorScreen theme={theme} onRetry={refetch} />
          </Else>
        </If>
        <Box mt={10} px={4}>
          <Copyright />
        </Box>
      </Box>
    );
  }

  const aqiInfo = getAQILevel(airQuality.aqi);
  const AQIIcon = aqiInfo.icon;
  const aqiPercentage = (airQuality.aqi / 500) * 100;

  return (
    <Box minH="100vh" bg={theme.background}>
      <PageHeader 
        theme={theme} 
        location={airQuality.location}
        availableLocations={availableLocations}
        currentLocationId={currentLocation.id}
        onLocationChange={setLocation}
      />
      <Container maxW="container.md" position="relative" zIndex={1} px={4} pt={2}>
        <Flex justify="flex-end">
          <Button
            size="xs"
            variant="ghost"
            color={theme.textMuted}
            onClick={refetch}
            isLoading={isLoading}
            leftIcon={<RefreshCw size={14} />}
            _hover={{ bg: theme.backgroundMuted }}
          >
            Odśwież
          </Button>
        </Flex>
      </Container>

      <Container maxW="container.md" py={8} px={4}>
        <VStack spacing={6} align="stretch">
          {/* Główny wskaźnik AQI */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            bg={theme.backgroundCard}
            borderRadius={THEME.borderRadiusXl}
            boxShadow={theme.shadowLg}
            border={`1px solid ${theme.borderCard}`}
            p={8}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              width="200px"
              height="200px"
              background={`radial-gradient(circle at 100% 0%, ${aqiInfo.color}15, transparent 70%)`}
              pointerEvents="none"
            />
            
            <Flex direction={{ base: "column", md: "row" }} align="center" gap={6}>
              <Box position="relative">
                <CircularProgress
                  value={aqiPercentage}
                  size="120px"
                  thickness="8px"
                  color={aqiInfo.color}
                  trackColor={theme.backgroundMuted}
                >
                  <CircularProgressLabel>
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="2xl"
                      fontWeight="700"
                      color={aqiInfo.color}
                    >
                      {airQuality.aqi}
                    </Text>
                  </CircularProgressLabel>
                </CircularProgress>
              </Box>
              
              <Flex direction="column" flex={1} gap={3}>
                <Flex align="center" gap={3}>
                  <Box
                    p={2}
                    borderRadius={THEME.borderRadius}
                    bg={`${aqiInfo.color}15`}
                    color={aqiInfo.color}
                  >
                    <Icon as={AQIIcon} boxSize={5} />
                  </Box>
                  <Badge
                    bg={aqiInfo.color}
                    color="white"
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontSize="md"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    boxShadow={`0 2px 10px ${aqiInfo.color}40`}
                  >
                    {aqiInfo.level}
                  </Badge>
                </Flex>
                
                <Text
                  color={theme.textSecondary}
                  fontSize="sm"
                  lineHeight="tall"
                  fontWeight="500"
                >
                  {aqiInfo.description}
                </Text>
                
                <Flex direction="column" gap={1}>
                  <Text
                    color={theme.textMuted}
                    fontSize="xs"
                    fontWeight="500"
                  >
                    Stacja: {airQuality.stationName}
                  </Text>
                  <Flex align="center" gap={2} flexWrap="wrap">
                    <Text
                      color={theme.textMuted}
                      fontSize="xs"
                      fontWeight="500"
                    >
                      Ostatnia aktualizacja: {new Date(airQuality.lastUpdate).toLocaleString("pl-PL")}
                    </Text>
                    {isFromCache && (
                      <Badge
                        bg={theme.warning}
                        color="white"
                        px={2}
                        py={0.5}
                        borderRadius="full"
                        fontSize="2xs"
                        fontWeight="600"
                      >
                        Z cache
                      </Badge>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </MotionBox>

          {/* Parametry zanieczyszczeń */}
          <Box>
            <Text
              fontFamily="'Outfit', sans-serif"
              fontSize="lg"
              fontWeight="700"
              color={theme.text}
              mb={4}
            >
              Parametry zanieczyszczeń
            </Text>
            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={4}
              flexWrap="wrap"
            >
              <Box flex="1" minW="150px">
                <AirQualityCard
                  label="PM2.5"
                  value={airQuality.pm25}
                  unit="μg/m³"
                  theme={theme}
                  color={getValueColor(airQuality.pm25, 25, theme)}
                />
              </Box>
              <Box flex="1" minW="150px">
                <AirQualityCard
                  label="PM10"
                  value={airQuality.pm10}
                  unit="μg/m³"
                  theme={theme}
                  color={getValueColor(airQuality.pm10, 50, theme)}
                />
              </Box>
            </Flex>
          </Box>

          {/* Warunki atmosferyczne */}
          <Box>
            <Text
              fontFamily="'Outfit', sans-serif"
              fontSize="lg"
              fontWeight="700"
              color={theme.text}
              mb={4}
            >
              Warunki atmosferyczne
            </Text>
            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={4}
              flexWrap="wrap"
            >
              <Box flex="1" minW="150px">
                <MotionBox
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  bg={theme.backgroundCard}
                  borderRadius={THEME.borderRadiusLg}
                  boxShadow={theme.shadowMd}
                  border={`1px solid ${theme.borderCard}`}
                  p={5}
                  _hover={{
                    boxShadow: theme.shadowLg,
                    transform: "translateY(-2px)",
                    borderColor: `${theme.primary}40`,
                  }}
                  style={{ transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }}
                >
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={Thermometer} boxSize={4} color={theme.primary} />
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="sm"
                      fontWeight="600"
                      color={theme.textMuted}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Temperatura
                    </Text>
                  </Flex>
                  <Flex align="baseline" gap={2}>
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="2xl"
                      fontWeight="700"
                      color={theme.text}
                    >
                      {airQuality.temperature}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={theme.textMuted}
                      fontWeight="500"
                    >
                      °C
                    </Text>
                  </Flex>
                </MotionBox>
              </Box>
              <Box flex="1" minW="150px">
                <MotionBox
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  bg={theme.backgroundCard}
                  borderRadius={THEME.borderRadiusLg}
                  boxShadow={theme.shadowMd}
                  border={`1px solid ${theme.borderCard}`}
                  p={5}
                  _hover={{
                    boxShadow: theme.shadowLg,
                    transform: "translateY(-2px)",
                    borderColor: `${theme.primary}40`,
                  }}
                  style={{ transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }}
                >
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={Droplets} boxSize={4} color={theme.brand} />
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="sm"
                      fontWeight="600"
                      color={theme.textMuted}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Wilgotność
                    </Text>
                  </Flex>
                  <Flex align="baseline" gap={2}>
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="2xl"
                      fontWeight="700"
                      color={theme.text}
                    >
                      {airQuality.humidity}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={theme.textMuted}
                      fontWeight="500"
                    >
                      %
                    </Text>
                  </Flex>
                </MotionBox>
              </Box>
              <Box flex="1" minW="150px">
                <MotionBox
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  bg={theme.backgroundCard}
                  borderRadius={THEME.borderRadiusLg}
                  boxShadow={theme.shadowMd}
                  border={`1px solid ${theme.borderCard}`}
                  p={5}
                  _hover={{
                    boxShadow: theme.shadowLg,
                    transform: "translateY(-2px)",
                    borderColor: `${theme.primary}40`,
                  }}
                  style={{ transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }}
                >
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={Gauge} boxSize={4} color={theme.accent} />
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="sm"
                      fontWeight="600"
                      color={theme.textMuted}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Ciśnienie
                    </Text>
                  </Flex>
                  <Flex align="baseline" gap={2}>
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="2xl"
                      fontWeight="700"
                      color={theme.text}
                    >
                      {airQuality.pressure}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={theme.textMuted}
                      fontWeight="500"
                    >
                      hPa
                    </Text>
                  </Flex>
                </MotionBox>
              </Box>
            </Flex>
          </Box>

          {/* Informacja o źródle danych */}
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            bg={theme.backgroundMuted}
            borderRadius={THEME.borderRadiusLg}
            p={4}
            border={`1px solid ${theme.border}`}
          >
            <Flex align="start" gap={3}>
              <Icon as={Info} boxSize={5} color={theme.textMuted} mt={0.5} />
              <Box>
                <Text
                  fontFamily="'Outfit', sans-serif"
                  fontSize="sm"
                  fontWeight="600"
                  color={theme.text}
                  mb={1}
                >
                  Informacja
                </Text>
                <Text
                  fontSize="xs"
                  color={theme.textSecondary}
                  lineHeight="tall"
                >
                  Dane pochodzą z systemu monitoringu jakości powietrza OSE (Ogólnopolska Sieć Edukacyjna). 
                  Wskaźnik AQI jest obliczany na podstawie wartości PM2.5 i PM10. Dane są aktualizowane automatycznie 
                  i przechowywane w pamięci podręcznej przez 1 godzinę.
                </Text>
              </Box>
            </Flex>
          </MotionBox>
        </VStack>

        <Box mt={10}>
          <Copyright />
        </Box>
      </Container>
    </Box>
  );
}

